/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';

/**
 * Lazy-loads the Yotpo reviews widget when its accordion is first opened.
 *
 * Expected markup (rendered by `blocks/_accordion-row.liquid` when the row's
 * `type === 'reviews'`):
 *
 * ```html
 * <details>
 *   <summary>…</summary>
 *   <yotpo-lazy-reviews>
 *     <template>
 *       <div class="yotpo-widget-instance" data-yotpo-instance-id="…" …></div>
 *     </template>
 *   </yotpo-lazy-reviews>
 * </details>
 * ```
 *
 * The widget markup lives inside a `<template>` so the Yotpo loader does NOT
 * pick it up on initial page scan. On the first `toggle` that opens the
 * accordion (or immediately, if the accordion is already `open` on mount),
 * the template is stamped into place and `window.yotpoWidgetsContainer
 * .initWidgets()` is called so the loader processes the new node.
 *
 * Yotpo's global loader script may not be ready when we stamp (it loads
 * async). We retry the init call up to 15 times at 200ms intervals — once
 * either `yotpoWidgetsContainer.initWidgets` or `yotpo.refreshWidgets`
 * resolves the call succeeds and we stop.
 *
 * Re-injection is guarded by `#loaded`; section morphs (variant / colourway
 * swaps) re-instantiate the element, which resets the guard and is fine.
 */
class YotpoLazyReviews extends Component {
  /** @type {boolean} */
  #loaded = false;
  /** @type {HTMLDetailsElement | null} */
  #details = null;

  connectedCallback() {
    super.connectedCallback();

    this.#details = this.closest('details');
    if (!this.#details) return;

    // Already open (e.g. open-by-default on desktop) → stamp now so the user
    // doesn't see an empty accordion body waiting on the next interaction.
    if (this.#details.open) {
      this.#injectWidget();
      return;
    }

    this.#details.addEventListener('toggle', this.#handleToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#details?.removeEventListener('toggle', this.#handleToggle);
  }

  /** @param {Event} event */
  #handleToggle = (event) => {
    const details = /** @type {HTMLDetailsElement} */ (event.currentTarget);
    if (details.open) this.#injectWidget();
  };

  #injectWidget() {
    if (this.#loaded) return;

    const template = this.querySelector(':scope > template');
    if (!(template instanceof HTMLTemplateElement)) return;

    this.#loaded = true;
    template.replaceWith(template.content.cloneNode(true));

    // Detach the toggle listener — work is done for the life of this element.
    this.#details?.removeEventListener('toggle', this.#handleToggle);

    this.#initYotpoWithRetry(15);
  }

  /** @param {number} attemptsLeft */
  #initYotpoWithRetry(attemptsLeft) {
    if (this.#tryInitYotpo()) return;
    if (attemptsLeft <= 0) return;
    setTimeout(() => this.#initYotpoWithRetry(attemptsLeft - 1), 200);
  }

  /** @returns {boolean} `true` if a Yotpo init API was reachable. */
  #tryInitYotpo() {
    try {
      const container = /** @type {{ initWidgets?: () => void } | undefined} */ (window.yotpoWidgetsContainer);
      if (typeof container?.initWidgets === 'function') {
        container.initWidgets();
        return true;
      }
      const yotpo = /** @type {{ refreshWidgets?: () => void } | undefined} */ (window.yotpo);
      if (typeof yotpo?.refreshWidgets === 'function') {
        yotpo.refreshWidgets();
        return true;
      }
    } catch (error) {
      console.error('[yotpo-lazy-reviews] init failed:', error);
    }
    return false;
  }
}

if (!customElements.get('yotpo-lazy-reviews')) {
  customElements.define('yotpo-lazy-reviews', YotpoLazyReviews);
}
